import React, { useState } from 'react';
import { ChevronDown, MessageCircle } from 'lucide-react';
import { useContact } from '../../hooks/useContact';
import { BRAND_FULL_NAME, BRAND_TEACHER } from '../../config/brand';
import HomeSectionFrame from './HomeSectionFrame';

const faqs = [
  {
    q: 'إيه الصفوف المتاحة على المنصة؟',
    a: 'المنصة بتغطي الصف الأول والثاني والثالث الثانوي. اختار صفك من قسم الصفوف في الصفحة الرئيسية وهتشوف الكورسات المناسبة ليك.',
  },
  {
    q: 'المنصة مجانية ولا مدفوعة؟',
    a: 'التسجيل على المنصة مجاني، والكورسات بعضها مجاني وبعضها باشتراك مدفوع — والسعر بيظهر على صفحة كل كورس قبل ما تشترك.',
  },
  {
    q: 'مين بيشرح على المنصة؟',
    a: `المنصة بإشراف ${BRAND_TEACHER} — شرح اللغة الإنجليزية بطريقة مبسّطة ومنظّمة لطلاب الثانوية.`,
  },
  {
    q: 'لو نسيت كلمة السر أعمل إيه؟',
    a: 'من صفحة تسجيل الدخول اختار «نسيت كلمة السر»، أدخل رقم هاتفك أو بريدك، وهتوصلك خطوات إعادة تعيين كلمة المرور.',
  },
  {
    q: 'إزاي أتواصل مع الدعم الفني؟',
    a: 'تقدر تستخدم زر واتساب في الصفحة، أو تتواصل مع فريق الدعم — وهنرد عليك في أقرب وقت خلال ساعات العمل.',
  },
];

const HomeFaq: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { openWhatsApp, whatsappNumber } = useContact();

  return (
    <HomeSectionFrame id="faq-section" aria-labelledby="faq-heading" tone="paper">
      <div className="mb-8 flex max-w-3xl flex-col gap-3 sm:mb-10">
        <h2 id="faq-heading" className="text-2xl font-extrabold text-slate-900 sm:text-3xl lg:text-4xl">
          أسئلة شائعة
        </h2>
        <p className="text-sm leading-relaxed text-slate-600 sm:text-base">
          إجابات مختصرة لأكثر الأسئلة عن {BRAND_FULL_NAME}.
        </p>
      </div>

      <div className="flex max-w-3xl flex-col overflow-hidden rounded-2xl border-2 border-brand-100 bg-slate-50/80">
        {faqs.map((item, index) => {
          const isOpen = openIndex === index;
          const num = String(index + 1).padStart(2, '0');
          const panelId = `faq-panel-${index}`;
          const buttonId = `faq-button-${index}`;

          return (
            <div
              key={item.q}
              className={`border-b border-brand-100 last:border-b-0 ${isOpen ? 'bg-brand-50/50' : ''}`}
            >
              <h3 className="m-0">
                <button
                  type="button"
                  id={buttonId}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpenIndex((prev) => (prev === index ? null : index))}
                  className="grid w-full cursor-pointer grid-cols-[auto_1fr_auto] items-start gap-x-4 gap-y-3 border-0 bg-transparent px-4 py-5 text-start text-slate-900 focus:outline-none sm:gap-x-5 sm:px-5 sm:py-6"
                >
                  <span className="pt-0.5 text-[0.8rem] font-bold tracking-wide text-brand-600" aria-hidden>
                    {num}
                  </span>
                  <span className="text-[1.05rem] font-bold leading-relaxed sm:text-[1.15rem]">
                    {item.q}
                  </span>
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-content-center text-brand-600" aria-hidden>
                    <ChevronDown
                      className={`h-[1.15rem] w-[1.15rem] transition-transform duration-150 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </span>
                </button>
              </h3>
              <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                hidden={!isOpen}
                className={isOpen ? 'px-4 pb-5 sm:px-5' : undefined}
              >
                {isOpen && (
                  <p className="m-0 max-w-[38rem] ps-10 text-[0.975rem] leading-[1.85] text-slate-600 sm:ps-12 sm:text-[1.025rem]">
                    {item.a}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {whatsappNumber && (
        <div className="mt-8 flex max-w-3xl justify-center">
          <button
            type="button"
            onClick={() => openWhatsApp('مرحباً، عندي سؤال عن المنصة')}
            className="inline-flex items-center gap-2 rounded-full border-2 border-green-300 bg-green-50 px-5 py-2.5 text-sm font-semibold text-green-800 transition hover:bg-green-100"
          >
            <MessageCircle className="h-4 w-4" />
            لسه عندك سؤال؟ تواصل معانا
          </button>
        </div>
      )}
    </HomeSectionFrame>
  );
};

export default HomeFaq;
